import { Link, usePage } from '@inertiajs/react';
import type { HexData } from '@/components/HexMap';

/**
 * Props for the FloatingNavButtons component.
 */
interface FloatingNavButtonsProps {
    /** The user's claimed hex, if any. When provided, shows the "My Hex" button. */
    userHex?: HexData | null;
    /** Whether this is the map page. On mobile, buttons only show on the map page. */
    isMapPage?: boolean;
}

/**
 * Shared page props that include the unread missive count.
 * Uses index signature to satisfy PageProps constraint.
 */
interface SharedPageProps {
    unreadMissiveCount?: number;
    [key: string]: unknown;
}

/**
 * Open the Tome in a new popup window.
 * The window is sized to look like a book.
 */
function openTome(e: React.MouseEvent) {
    e.preventDefault();

    // Open in a new window with book-like dimensions
    const width = 1000;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
        '/tome',
        'archivists-tome',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
}

/**
 * Open the University in a new popup window.
 * The window is sized to look like a study guide.
 */
function openUniversity(e: React.MouseEvent) {
    e.preventDefault();

    // Open in a new window with study guide dimensions
    const width = 900;
    const height = 700;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
        '/university',
        'sellune-university',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
}

/**
 * Open the Feywild Times newspaper in a new popup window.
 * The window is sized to look like a newspaper.
 */
function openNewspaper(e: React.MouseEvent) {
    e.preventDefault();

    // Open in a new window with newspaper-like dimensions
    const width = 1024;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
        '/newspaper',
        'faewild-times',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
}

/**
 * Open the Missives inbox in a new popup window.
 * The window is sized to look like a letter (portrait orientation).
 */
function openMissives(e: React.MouseEvent) {
    e.preventDefault();

    // Open in a new window with letter-like portrait dimensions
    const width = 600;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
        '/missives',
        'missives-inbox',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
}

/**
 * HeaderNavButtons - Navigation buttons for the header bar (desktop).
 * Renders text links styled with dark, bold text for visibility against leaf canopy.
 */
export function HeaderNavButtons({ userHex }: FloatingNavButtonsProps) {
    // Get unread missive count from shared page props
    const { unreadMissiveCount = 0 } = usePage<SharedPageProps>().props;

    const linkClass = "text-green-950 font-bold hover:text-white transition-colors cursor-pointer [text-shadow:_0_0_8px_rgba(255,255,255,0.8),_0_0_2px_rgba(255,255,255,1)]";

    return (
        <div className="hidden md:flex items-center gap-4">
            {/* Tome Link */}
            <button
                onClick={openTome}
                className={linkClass}
                title="Open Archivist's Tome"
            >
                Tome
            </button>

            {/* University Link */}
            <button
                onClick={openUniversity}
                className={linkClass}
                title="Open University - How to Play"
            >
                University
            </button>

            {/* Newspaper Link */}
            <button
                onClick={openNewspaper}
                className={linkClass}
                title="Open The Feywild Times"
            >
                News
            </button>

            {/* Missives Link with unread badge */}
            <button
                onClick={openMissives}
                className={`${linkClass} flex items-center gap-1`}
                title="Open Missives"
            >
                Missives
                {unreadMissiveCount > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                        {unreadMissiveCount > 99 ? '99+' : unreadMissiveCount}
                    </span>
                )}
            </button>

            {/* My Hex Link - only shown if user has a hex */}
            {userHex && (
                <>
                    <Link
                        href={`/hex/${userHex.id}`}
                        className={linkClass}
                        title="Go to My Hex"
                    >
                        My Hex
                    </Link>
                    {/* Adventures Link */}
                    <Link
                        href={`/hex/${userHex.id}?tab=adventures`}
                        className={linkClass}
                        title="Adventures"
                    >
                        Adventures
                    </Link>
                </>
            )}
        </div>
    );
}

/**
 * MobileBottomNav - Floating navigation buttons for mobile (only on map page).
 * Renders floating buttons at the bottom of the screen without a background bar.
 */
export function MobileBottomNav({ userHex }: FloatingNavButtonsProps) {
    // Get unread missive count from shared page props
    const { unreadMissiveCount = 0 } = usePage<SharedPageProps>().props;

    return (
        <div className="md:hidden fixed bottom-4 left-0 right-0 z-50 px-4 pointer-events-none safe-area-inset-bottom">
            <div className="flex items-center justify-center gap-6 pointer-events-auto">
                {/* Tome Button */}
                <button
                    onClick={openTome}
                    className="flex flex-col items-center gap-1 text-amber-200 hover:text-amber-100 transition-colors"
                    title="Open Archivist's Tome"
                >
                    <span className="w-14 h-14 bg-gradient-to-br from-amber-800 to-amber-900 border-2 border-amber-600/50 rounded-full shadow-lg flex items-center justify-center text-2xl">
                        📖
                    </span>
                    <span className="text-xs font-medium drop-shadow-md">Tome</span>
                </button>

                {/* University Button */}
                <button
                    onClick={openUniversity}
                    className="flex flex-col items-center gap-1 text-amber-200 hover:text-amber-100 transition-colors"
                    title="Open University - How to Play"
                >
                    <span className="w-14 h-14 bg-gradient-to-br from-blue-800 to-blue-900 border-2 border-blue-600/50 rounded-full shadow-lg flex items-center justify-center text-2xl">
                        🎓
                    </span>
                    <span className="text-xs font-medium drop-shadow-md">University</span>
                </button>

                {/* Newspaper Button */}
                <button
                    onClick={openNewspaper}
                    className="flex flex-col items-center gap-1 text-amber-200 hover:text-amber-100 transition-colors"
                    title="Open The Feywild Times"
                >
                    <span className="w-14 h-14 bg-gradient-to-br from-amber-700 to-amber-800 border-2 border-amber-500/50 rounded-full shadow-lg flex items-center justify-center text-2xl">
                        📰
                    </span>
                    <span className="text-xs font-medium drop-shadow-md">News</span>
                </button>

                {/* Missives Button with unread badge */}
                <button
                    onClick={openMissives}
                    className="flex flex-col items-center gap-1 text-amber-200 hover:text-amber-100 transition-colors relative"
                    title="Open Missives"
                >
                    <span className="relative w-14 h-14 bg-gradient-to-br from-amber-800 to-amber-900 border-2 border-amber-600/50 rounded-full shadow-lg flex items-center justify-center text-2xl">
                        ✉️
                        {unreadMissiveCount > 0 && (
                            <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full">
                                {unreadMissiveCount > 99 ? '99+' : unreadMissiveCount}
                            </span>
                        )}
                    </span>
                    <span className="text-xs font-medium drop-shadow-md">Missives</span>
                </button>

                {/* My Hex Button - only shown if user has a hex */}
                {userHex && (
                    <Link
                        href={`/hex/${userHex.id}`}
                        className="flex flex-col items-center gap-1 text-amber-200 hover:text-amber-100 transition-colors"
                        title="Go to My Hex"
                    >
                        <span className="w-14 h-14 bg-gradient-to-br from-amber-800 to-amber-900 border-2 border-amber-600/50 rounded-full shadow-lg flex items-center justify-center text-2xl">
                            🏠
                        </span>
                        <span className="text-xs font-medium drop-shadow-md">My Hex</span>
                    </Link>
                )}

                {/* Adventures Button */}
                <Link
                    href="/adventures"
                    className="flex flex-col items-center gap-1 text-purple-200 hover:text-purple-100 transition-colors"
                    title="Adventures"
                >
                    <span className="w-14 h-14 bg-gradient-to-br from-purple-800 to-purple-900 border-2 border-purple-600/50 rounded-full shadow-lg flex items-center justify-center text-2xl">
                        ⚔️
                    </span>
                    <span className="text-xs font-medium drop-shadow-md">Adventures</span>
                </Link>
            </div>
        </div>
    );
}

/**
 * FloatingNavButtons - Floating navigation buttons.
 *
 * On desktop (md+): Hidden - use HeaderNavButtons in the header instead.
 * On mobile: Shows a fixed bottom navigation bar, but only on the map page.
 *
 * Includes:
 * - Tome button: Opens the Archivist's Tome in a new window
 * - Missives button: Navigates to missives inbox with unread badge
 * - My Hex button: Navigates to the user's hex page (only shown if user has a hex)
 * - Adventures button: Navigates to the adventures page
 */
export function FloatingNavButtons({ userHex, isMapPage = false }: FloatingNavButtonsProps) {
    // On mobile, only show on the map page
    if (!isMapPage) {
        return null;
    }

    return <MobileBottomNav userHex={userHex} />;
}

/**
 * FloatingTomeIcon - Legacy export for backwards compatibility.
 * Use FloatingNavButtons instead for new code.
 * @deprecated Use FloatingNavButtons instead
 */
export function FloatingTomeIcon() {
    return <FloatingNavButtons />;
}

